import React, { useState, useEffect, useRef } from 'react';
import { Map, List, fromJS } from 'immutable';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage, defineMessages } from 'react-intl';
import { usePrevious } from 'hooks';
import { setBrowseInfo, fetchProduct } from 'actions/browse';
import ProductDetails from 'components/ProductDetails';
import KioskProductDetails from 'components/KioskProductDetails';
import FavouriteButton from 'components/Product/ProductAdditional/FavouriteButton';
import { AlertFooter } from 'assets/styles/sharedStyles';
import ProductQuantity from 'components/ProductQuantity';
import { customiseProduct, addProductToOrder } from 'actions/order';
import { fetchSection } from 'actions/menu';
import { fetchComboUpsells } from 'actions/comboUpsells';
import Loading from 'components/Loading';
import {
  selectMenuType,
  productHasModifiers,
  selectDefaultModifiers,
  showInstantAddButton,
  getItemPrice,
  getMenuSectionById,
  selectSectionProducts,
  backToItemMenuPage,
  getProductById,
  productHasCombo,
} from 'selectors/browse';
import {
  getVenueId,
  getServiceId,
  getServiceById,
  isDeliveryToAddressEnabled,
  isServiceBrowseOnly,
  getVenueOrganisationId,
  getServiceComboUpsellsEnabled,
} from 'selectors/root';
import { shouldAllowItemNotes } from 'selectors/features';
import {
  openComboUpsellAlert,
  openEnterNameAlert,
  openRadiusAlert,
  openProductInfo,
  clearAlerts,
} from 'actions/UI';
import { isLoggedIn, selectIsUserAddressInRange } from 'selectors/user';
import { reactPixel, gtmDataLayerPush, addCssPrefixTo } from 'utils';
import { trackEvent } from 'utils/tracking';
import styled from 'styled-components';
import {
  selectComboUpsellsLoading,
  selectComboUpsells,
  selectComboUpsellsHaveBeenFetched,
} from 'selectors/comboUpsells';
import {
  InfoContainer,
  StyledContent,
  ButtonsContainer,
  FlexBox,
  StyledActionButtons,
  ButtonsWrapper,
} from './styles';
import { AddToOrder } from './AddToOrder';
import { Customise } from './Customise';

const messages = defineMessages({
  customise: {
    defaultMessage: 'Customise',
  },
  customiseOptions: {
    defaultMessage: 'Customisation Options',
  },
});

const FlexContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const propTypes = {
  defaultModifiers: PropTypes.instanceOf(List),
  product: PropTypes.instanceOf(Map).isRequired,
  pageView: PropTypes.bool,
  slimProducts: PropTypes.bool,
  fetchSection: PropTypes.func.isRequired,
  fetchProduct: PropTypes.func.isRequired,
  fetchComboUpsells: PropTypes.func.isRequired,
  sectionId: PropTypes.string,
  setBrowseInfo: PropTypes.func,
  item: PropTypes.instanceOf(Map).isRequired,
  menuType: PropTypes.object,
  serviceId: PropTypes.string,
  venueId: PropTypes.number,
  organisationId: PropTypes.number,
  customiseProduct: PropTypes.func,
  addToOrder: PropTypes.func,
  isLoggedIn: PropTypes.bool,
  isDeliveryToAddressEnabled: PropTypes.bool,
  isAddressUserInRange: PropTypes.bool,
  getItemPrice: PropTypes.func,
  browseOnly: PropTypes.bool,
  allowItemNotes: PropTypes.bool,
  openRadiusAlert: PropTypes.func,
  closeAlert: PropTypes.func,
  clearAlerts: PropTypes.func,
  backToItemMenuPage: PropTypes.func,
  backToItemPage: PropTypes.func,
  openEnterNameAlert: PropTypes.func,
  openComboUpsellAlert: PropTypes.func,
  openUpsellProductInfo: PropTypes.func,
  comboUpsellsEnabledForService: PropTypes.bool,
  comboUpsells: PropTypes.instanceOf(List),
  comboUpsellsLoading: PropTypes.bool,
  comboUpsellsFetched: PropTypes.bool,
  /** Current number of alerts on the stack; used to close the combo upsell by index when it is pushed. */
  alertStackSize: PropTypes.number,
};
const ProductOptionInformation = ({
  defaultModifiers,
  product,
  pageView,
  slimProducts,
  fetchSection,
  fetchProduct,
  fetchComboUpsells,
  sectionId,
  venueId,
  setBrowseInfo,
  item,
  menuType,
  serviceId,
  customiseProduct,
  addToOrder,
  isLoggedIn,
  isDeliveryToAddressEnabled,
  isAddressUserInRange,
  getItemPrice,
  browseOnly,
  allowItemNotes,
  openRadiusAlert,
  closeAlert,
  clearAlerts,
  backToItemMenuPage,
  openEnterNameAlert,
  openComboUpsellAlert,
  openUpsellProductInfo,
  comboUpsellsEnabledForService,
  comboUpsells,
  comboUpsellsLoading,
  comboUpsellsFetched,
  organisationId,
  alertStackSize,
}) => {
  // Add venueId to array to turn on kiosk product details component
  const kiosk =
    [111, 1477, 1295, 1657, 1658, 1659, 1660, 1661, 1814, 1926, 1938].includes(venueId) ||
    [168, 182, 189, 192, 247].includes(organisationId);

  const notesState = useState('');
  const [notes] = notesState;

  const [itemEnteredName, setItemEnteredName] = useState('');

  // Productquantity
  const [quantity, setQuantity] = useState(1);

  // Modifies
  const [defaultMods, setDefaultMods] = useState(defaultModifiers || null);

  // Product
  const [prod, setProd] = useState(product);

  // Slim products
  const [prevSlimProducts] = usePrevious(slimProducts);

  const [addedToBasket, setAddedToBasket] = useState(false);

  // Combo upsell alert
  const [comboUpsellShown, setComboUpsellShown] = useState(false);
  const alertStackSizeRef = useRef(alertStackSize);
  useEffect(() => {
    alertStackSizeRef.current = alertStackSize;
  }, [alertStackSize]);

  // Only try to fetch combo upsells if an item is not a combo itself, and it's a combo group product
  const shouldFetchComboUpsells = item => {
    if (!comboUpsellsEnabledForService) return false;
    if (item.getIn(['product', 'combo'])) return false;
    return item.getIn(['product', 'is_combo_group_product']);
  };

  useEffect(() => {
    if (pageView && slimProducts) fetchSection(sectionId);
  }, [pageView, slimProducts, fetchSection, sectionId]);

  useEffect(() => {
    if (prevSlimProducts && !slimProducts) setDefaultMods(defaultModifiers);
  }, [prevSlimProducts, slimProducts, defaultModifiers]);

  useEffect(() => {
    if (itemEnteredName) {
      addToOrderCallback();
    }
  }, [itemEnteredName]);

  useEffect(() => {
    if (addedToBasket) {
      clearAlerts();
    }
  });

  // Modifiers
  useEffect(() => {
    const prod = baseModifierValue ? baseModifierValue.get('product') : product;
    setProd(prod);

    if (pageView) {
      setBrowseInfo(prod.get('name'), '', prod.getIn(['images', 0, 'path']));
    }
  }, [baseModifierValue, product, defaultMods, pageView, setBrowseInfo]);

  // reset combo upsell shown when item changes
  useEffect(() => {
    setComboUpsellShown(false);
  }, [item?.get('id')]);

  // fetch combo upsells under specific conditions
  useEffect(() => {
    if (!item?.get('id') || !menuType?.get('id')) return;
    if (!comboUpsellsEnabledForService) return;
    if (item.getIn(['product', 'combo'])) return;
    if (!item.getIn(['product', 'is_combo_group_product'])) return;

    // Already fetched (even if empty) => don't dispatch / don't hit backend
    if (comboUpsellsFetched) return;

    fetchComboUpsells(item.get('id'), menuType.get('id'), serviceId);
  }, [item, menuType, comboUpsellsEnabledForService, comboUpsellsFetched, fetchComboUpsells, serviceId]);

  // if there are combo upsells show the component
  useEffect(() => {
    if (comboUpsellShown) return; // already shown
    if (comboUpsellsLoading) return; // wait for fetch to finish
    if (!comboUpsells || comboUpsells.size === 0) return; // no upsells

    // for safety, ensure upsells belong to this item
    if (comboUpsells.first()?.get('original_section_product_id') !== item.get('id')) {
      return;
    }

    setComboUpsellShown(true);

    // Capture the current stack size before the push — the new alert lands at this index.
    const comboUpsellIndex = alertStackSizeRef.current ?? 1;
    openComboUpsellAlert(
      menuType.get('id'),
      serviceId,
      comboUpsells,
      comboItem => {
        // After product is fetched trigger opening the product info for the chosen combo
        fetchProduct(comboItem.get('productId')).then(() => {
          openUpsellProductInfo(comboItem);
        });
      },
      () => closeAlert(null, comboUpsellIndex)
    );
  }, [
    comboUpsells,
    comboUpsellsLoading,
    comboUpsellShown,
    item,
    serviceId,
    openComboUpsellAlert,
    closeAlert,
    menuType,
  ]);

  const updateBaseModifierValue = value => {
    setDefaultMods(prevStateDefaultMods => {
      const defaultMods = prevStateDefaultMods;
      const index = defaultMods.findIndex(
        modifier => modifier.getIn(['modifier', 'display_type']) === 'base'
      );
      return defaultMods.setIn([index, 'values'], fromJS([value]));
    });
  };
  const baseModifier = defaultMods
    ? defaultMods.find(modifier => modifier.getIn(['modifier', 'display_type']) === 'base')
    : null;

  const baseModifierValue = baseModifier?.getIn(['values', 0]);

  // Customise
  const handleCustomiseProduct = () => {
    reactPixel.track('CustomizeProduct');
    gtmDataLayerPush('CustomizeProduct');

    customiseProduct(item, menuType, serviceId, quantity, notes, defaultMods, prod);
  };

  // Handling add to order
  const handleAddToOrder = () => {
    reactPixel.track('AddToCart', { content_name: prod?.get('name', '') });
    gtmDataLayerPush('AddToCart', { content_name: prod?.get('name', '') });

    const finalNotes = itemEnteredName ? itemEnteredName + (notes ? `\n${notes}` : '') : notes;

    addToOrder(item, menuType, serviceId, finalNotes, quantity, defaultMods);
  };

  // Unit price
  let unitPrice = menuType?.get('id')
    ? item.getIn(['prices', menuType.get('id').toString(), 'price'])
    : undefined;
  if (baseModifierValue) {
    unitPrice = baseModifierValue.getIn(['product', 'price']);
  }

  // Product price
  const price = getItemPrice(unitPrice, prod.get('tax_categories')) || undefined;

  const showRadiusModal = !isLoggedIn && isDeliveryToAddressEnabled && !isAddressUserInRange;

  const showCustomise =
    prod &&
    (productHasModifiers(prod, true) ||
      productHasModifiers(item.get('product'), true) ||
      productHasCombo(prod));

  const showAddToOrder =
    !browseOnly && (!showCustomise || (showInstantAddButton(product) && showInstantAddButton(prod)));

  const closePage = () => {
    if (closeAlert) closeAlert();
    else if (pageView) backToItemMenuPage();
  };

  const addToOrderCallback = () => {
    if (!itemEnteredName && item.getIn(['product', 'tags'])?.includes('requires-name')) {
      openEnterNameAlert(setItemEnteredName);

      return;
    }

    if (showRadiusModal) {
      openRadiusAlert();

      return;
    }

    handleAddToOrder();

    if (closeAlert) closeAlert();
    else if (pageView) backToItemMenuPage();

    setAddedToBasket(true);
  };

  const customiseCallback = () => {
    showRadiusModal ? openRadiusAlert() : handleCustomiseProduct();
    if (showInstantAddButton(product)) {
      trackEvent('customise_item', {
        category: 'item',
      });
    }
  };

  const customiseLabel = browseOnly ? (
    <FormattedMessage {...messages.customiseOptions} />
  ) : (
    <FormattedMessage {...messages.customise} />
  );

  const itemCanHaveComboUpsells = item?.get('id') && menuType?.get('id') && shouldFetchComboUpsells(item);

  const shouldShowComboUpsellAlert =
    itemCanHaveComboUpsells &&
    comboUpsellsFetched &&
    comboUpsells &&
    comboUpsells.size > 0 &&
    !comboUpsellShown;

  const shouldShowComboUpsellLoading =
    itemCanHaveComboUpsells && (comboUpsellsLoading || (!comboUpsellsFetched && !comboUpsellShown));

  // show a loading spinner when combo upsell alert is to be shown
  if (!prod || shouldShowComboUpsellLoading || shouldShowComboUpsellAlert) {
    return (
      <InfoContainer pageView={pageView}>
        <div style={{ minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loading />
        </div>
      </InfoContainer>
    );
  }

  if (!kiosk)
    return (
      <FlexContainer>
        <ProductDetails
          product={prod?.toJS()}
          productImages={prod.get('images')?.toJS()}
          productDescription={prod.get('description')}
          baseModifier={baseModifier?.get('modifier')}
          selectedBaseModifierId={baseModifierValue?.get('id')}
          changeModifier={updateBaseModifierValue}
          notesState={notesState}
          price={price}
          pageView={pageView}
          browseOnly={browseOnly}
          onClose={closePage}
        />
        <AlertFooter>
          <ButtonsContainer className={addCssPrefixTo('lines')}>
            <FlexBox allowItemNotes={allowItemNotes}>
              <ProductQuantity
                quantity={quantity}
                onChange={value => setQuantity(value)}
                productId={product?.get('id')}
              />
            </FlexBox>
          </ButtonsContainer>
        </AlertFooter>
        <StyledActionButtons pageView={pageView}>
          {showCustomise && <Customise callback={customiseCallback} label={customiseLabel} />}
          {showAddToOrder && <AddToOrder callback={addToOrderCallback} />}
        </StyledActionButtons>
      </FlexContainer>
    );

  return (
    <>
      <StyledContent pageView={pageView}>
        <KioskProductDetails
          product={prod}
          baseModifier={baseModifier?.get('modifier')}
          selectedBaseModifierId={baseModifierValue?.get('id')}
          changeModifier={updateBaseModifierValue}
          price={price}
          productImages={pageView ? new List() : prod.get('images')}
          productDescription={prod.get('description')}
          pageView={pageView}
          additionalButtons={[
            <FavouriteButton smallBtn={pageView} key={product.get('id')} product={product} />,
          ]}
          quantity={
            <ProductQuantity
              quantity={quantity}
              onChange={value => setQuantity(value)}
              productId={product?.get('id')}
            />
          }
        />
      </StyledContent>
      <ButtonsWrapper>
        {showCustomise && <Customise callback={customiseCallback} label={customiseLabel} />}
        {showAddToOrder && <AddToOrder callback={addToOrderCallback} />}
      </ButtonsWrapper>
    </>
  );
};
ProductOptionInformation.propTypes = propTypes;
const mapStateToProps = (state, ownProps) => {
  let item = ownProps?.item;
  let section;
  const serviceId = getServiceId(state);
  const venueId = getVenueId(state);
  const organisationId = getVenueOrganisationId(state);
  const productId = ownProps.params?.productId;
  const menuId = ownProps.params?.menuId;
  const sectionId = ownProps.params?.sectionId;
  const menuType = selectMenuType(state);
  if (!item && productId) {
    section = getMenuSectionById(state, menuId, sectionId);
    const items = selectSectionProducts(state, section, getServiceById(state, serviceId));
    item = items.find(item => item.get('productId') === Number(productId));
  }
  const product = item ? getProductById(state, item.get('productId')) : null;
  const sectionProductId = item?.get('id');

  return {
    isLoggedIn: isLoggedIn(state),
    isDeliveryToAddressEnabled: isDeliveryToAddressEnabled(state),
    isAddressUserInRange: selectIsUserAddressInRange(state),
    pageView: Boolean(productId),
    sectionId,
    item,
    product,
    slimProducts: section && section.get('slimProducts'),
    menuType,
    serviceId,
    defaultModifiers: selectDefaultModifiers(product),
    getItemPrice: (price, taxCategories) => getItemPrice(state, price, taxCategories),
    backToItemMenuPage: () => backToItemMenuPage(state, item),
    allowItemNotes: shouldAllowItemNotes(state),
    browseOnly: isServiceBrowseOnly(state, serviceId),
    venueId,
    organisationId,
    comboUpsellsEnabledForService: getServiceComboUpsellsEnabled(state, serviceId),
    comboUpsells: sectionProductId ? selectComboUpsells(state, sectionProductId) : new List(),
    comboUpsellsLoading: selectComboUpsellsLoading(state),
    comboUpsellsFetched: sectionProductId
      ? selectComboUpsellsHaveBeenFetched(state, sectionProductId)
      : false,
  };
};

const mapDispatchToProps = dispatch => ({
  setBrowseInfo: (title, description, imagePath) => dispatch(setBrowseInfo(title, description, imagePath)),
  openRadiusAlert: () => dispatch(openRadiusAlert()),
  clearAlerts: () => dispatch(clearAlerts()),
  openEnterNameAlert: setName => dispatch(openEnterNameAlert(setName)),
  openUpsellProductInfo: item => dispatch(openProductInfo(item)),
  openComboUpsellAlert: (menuTypeId, serviceId, comboUpsellList, onAccept, onDecline) =>
    dispatch(openComboUpsellAlert(menuTypeId, serviceId, comboUpsellList, onAccept, onDecline)),
  fetchSection: id => dispatch(fetchSection(id)),
  fetchProduct: id => dispatch(fetchProduct(id)),
  fetchComboUpsells: (sectionProductId, menuTypeId, serviceId) =>
    dispatch(fetchComboUpsells(sectionProductId, menuTypeId, serviceId)),
  customiseProduct: (
    item,
    menuType,
    serviceId,
    quantity,
    notes,
    modifiers = undefined,
    baseModifierProduct = undefined
  ) => {
    item = item.merge({
      quantity,
      notes,
      modifiers,
      // The skeleton / parent product id
      base_product_id: item.get('productId'),
      // The product that has been selected in the base modifier selection
      productId: baseModifierProduct.get('id'),
    });

    dispatch(customiseProduct(item, menuType, serviceId, 'customiseFromOnInfo'));
  },
  addToOrder: (item, menuType, serviceId, notes, quantity, modifiers = undefined) =>
    dispatch(addProductToOrder(item, menuType, serviceId, modifiers, notes, quantity)),
});
export default connect(mapStateToProps, mapDispatchToProps)(ProductOptionInformation);
